<template>
  <div
v-if="designerStore.loading"
class="tw"
>
    <div class="tw-h-screen tw-flex tw-items-center tw-justify-center">
      <VueSpinnerOrbit size="8em" />
    </div>
  </div>
  <div
      v-else
      class="tw-container tw-mx-auto tw-px-4 tw-py-6"
  >
    <div v-if="coreStore.leadGen && coreStore.leadGen.multiple && !selectedProduct && !showCart">
      <ProductSelectionGrid 
        :products="coreStore.leadGen.products" 
        :cart-items-count="cartItems.length"
        :settings="coreStore.leadGenSettings"
        @product-selected="handleProductSelected"
        @view-quote="showCart = true"
      />
    </div>
    <div v-else-if="coreStore.leadGen && coreStore.leadGen.multiple && showCart">
      <Quote 
        :quote-items="cartItems"
        :settings="coreStore.leadGenSettings" 
        @add-another-product="addAnotherProduct" 
        @remove-from-quote="handleRemoveFromQuote" 
        @show-quote-form="handleShowQuoteForm"
        @quote-emptied="handleQuoteEmptied"
/>
    </div>
    <div v-else-if="coreStore.leadGen && coreStore.leadGen.multiple && selectedProduct">
      <LeadGeneratorLayout 
        :lead-gen="coreStore.leadGen" 
        :settings="coreStore.leadGenSettings" 
        :selected-product="selectedProduct" 
        :cart-items-count="cartItems.length"
        @add-to-cart="handleAddToCart" 
        @submit-quote="handleSubmitQuote"
        @back-to-products="backToProducts"
        @view-quote="showCart = true"
        @clear-cart="clearCart"
      />
    </div>
    <div v-else-if="coreStore.leadGen && !coreStore.leadGen.multiple">
      <LeadGeneratorLayout 
        :lead-gen="coreStore.leadGen" 
        :settings="coreStore.leadGenSettings" 
        :selected-product="selectedProduct" 
        :cart-items-count="cartItems.length"
        @add-to-cart="handleAddToCart" 
        @show-quote-form="handleShowQuoteForm"
        @back-to-products="backToProducts"
        @view-quote="showCart = true"
        @clear-cart="clearCart"
      />
    </div>
    <div v-else>
      Loading...
    </div>
  </div>

  <!-- Quote Form Modal -->
  <QuoteFormModal
    :show="showQuoteFormModal"
    :lead-fields="coreStore.leadGen ? coreStore.leadGen.lead_fields : []"
    :cart-items-count="coreStore.leadGen && !coreStore.leadGen.multiple ? 1 : cartItems.length"
    :product-details="getProductDetailsForModal()"
    :primary-color="coreStore.leadGenSettings ? coreStore.leadGenSettings.primary_colour : '#10b981'"
    @submit="handleModalSubmit"
    @close="showQuoteFormModal = false"
  />
</template>

<script>
import {useDesignerStore} from '@/stores/DesignerStore';
import {VueSpinnerOrbit} from "vue3-spinners";
import {inject, ref, onMounted} from "vue";
import LeadGeneratorLayout from "@/components/backend/pages/lead-generator/components/LeadGeneratorLayout.vue";
import ProductSelectionGrid from "@/components/backend/pages/lead-generator/components/ProductSelectionGrid.vue";
import Quote from "@/components/backend/pages/lead-generator/components/Quote.vue";
import QuoteFormModal from "@/components/backend/pages/lead-generator/components/QuoteFormModal.vue";
import {useCoreStore} from "@/stores/CoreStore";

export default {
  name: "LeadGeneratorPage",
  components: {
    LeadGeneratorLayout,
    VueSpinnerOrbit,
    ProductSelectionGrid,
    Quote,
    QuoteFormModal,
  },
  setup() {
    const designerStore = useDesignerStore();
    const coreStore = useCoreStore();
    const designerService = inject('designerService');

    const selectedProduct = ref(null);
    const cartItems = ref([]);
    const showCart = ref(false);
    const showQuoteFormModal = ref(false);
    const formData = ref({});

    const handleProductSelected = async (product) => {
      designerStore.setLoading(true);
      selectedProduct.value = product;
      showCart.value = false;
      await designerService.getProduct(product.designer_id);
      designerStore.setLoading(false);

    };

    const handleAddToCart = (productDetails) => {
      cartItems.value.push(productDetails);
      showCart.value = true;
      selectedProduct.value = null;
    };

    const addAnotherProduct = () => {
      selectedProduct.value = null;
      showCart.value = false;
    };

    const backToProducts = () => {
      selectedProduct.value = null;
      showCart.value = false;
    };

    const handleRemoveFromQuote = (index) => {
      cartItems.value.splice(index, 1);
    };

    const handleQuoteEmptied = () => {
      // Automatically go back to product selection when quote becomes empty
      showCart.value = false;
    };

    const clearCart = () => {
      cartItems.value = [];
    }

    const handleSubmitQuote = async ({ formData, productDetails }) => {
      // Get flow data from the store if it exists
      const flowData = designerStore.getFlowDataForSubmission();
      
      // productDetails is already in the correct format from getProductDetailsForModal
      // For single product: object with name, description, raw_data
      // For multi-product: array of cart items
      let productsToSubmit = productDetails;
      
      // Combine form data with flow data
      const submissionData = {
        leadFormData: formData,
        productDetails: productsToSubmit,
        flowData: flowData.flowUuid ? flowData : null  // Only include flow data if it exists
      };
      
      console.log('Submitting quote with data:', submissionData);
      
      await designerService.submitLead(formData, productsToSubmit, flowData);
      
      // Clear the flow data after successful submission
      designerStore.clearFlowData();
      
      // Clear the cart and close modal
      cartItems.value = [];
      showCart.value = false;
      showQuoteFormModal.value = false;
    };

    const handleShowQuoteForm = () => {
      showQuoteFormModal.value = true;
    };

    const handleModalSubmit = async (data) => {
      await handleSubmitQuote(data);
    };

    const getProductDetailsForModal = () => {
      return cartItems.value;
    };

    const capitalize = (s) => {
      return s && String(s[0]).toUpperCase() + String(s).slice(1);
    };

    onMounted(async () => {
      let leadGenData = await designerService.getLeadGenSettings();

      if (leadGenData) {
        coreStore.leadGen = leadGenData.leadGen;
        coreStore.leadGenSettings = leadGenData.settings;

        if (!leadGenData.leadGen.multiple && leadGenData.leadGen.product_id) {
          designerService.getProduct(leadGenData.leadGen.product_id);
        } else {
          designerStore.setLoading(false)
        }
      } else {
        coreStore.page = 'pg-not-found';
      }
    });

    return {
      designerStore,
      coreStore,
      selectedProduct,
      cartItems,
      showCart,
      showQuoteFormModal,
      formData,
      handleProductSelected,
      handleAddToCart,
      addAnotherProduct,
      backToProducts,
      handleRemoveFromQuote,
      handleQuoteEmptied,
      handleSubmitQuote,
      handleShowQuoteForm,
      handleModalSubmit,
      getProductDetailsForModal,
      capitalize,
      clearCart
    };
  },
};
</script>

<style>
.cc-active:before{
  content: "✔";
  position: absolute;
  z-index: 100;
  top: 0px;
  left: 0px;
  display: block;
  padding: 0;
  width: 25px;
  height: 25px;
  font-size: 13px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background-color: #3399bb;
  border-radius: 6px 0 0 ;
  color: #fff;
}
</style>
